/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { format, parseISO } from 'date-fns';
import { Star } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { type Therapist } from '../../lib/api/resources.js';
import { cn } from '../../lib/utils/cn.js';

/** Two-letter monogram for a therapist (drops a leading "Dr."), e.g. "Dr. Michael Brown" → "MB". */
export function therapistInitials(name: string): string {
  const parts = name
    .replace(/^Dr\.?\s*/i, '')
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  const a = parts[0]?.[0] ?? '';
  const b = parts[1]?.[0] ?? '';
  const result = `${a}${b}`.toUpperCase().slice(0, 2);
  return result === '' ? '?' : result;
}

function Stat({ value, label }: { readonly value: string; readonly label: string }): JSX.Element {
  return (
    <div className="rounded-xl bg-slate-50 py-3 text-center dark:bg-slate-800">
      <p className="text-sm font-bold text-slate-800 dark:text-white">{value}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  );
}

export function Stars({ rating }: { readonly rating: number }): JSX.Element {
  const filled = Math.round(rating);
  return (
    <div className="flex" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={cn(
            'h-4 w-4',
            n <= filled ? 'fill-amber-400 text-amber-400' : 'text-slate-300',
          )}
        />
      ))}
    </div>
  );
}

interface TherapistCardProps {
  readonly therapist: Therapist;
  readonly onSelect: (therapist: Therapist) => void;
}

/** One therapist card (Figma "Therapist Management"). Click opens the profile. */
export function TherapistCard({ therapist, onSelect }: TherapistCardProps): JSX.Element {
  const { t } = useTranslation();
  const joined =
    therapist.joinedOn !== null ? format(parseISO(therapist.joinedOn), 'MMM yyyy') : '';
  const rating = therapist.rating;
  const select = useCallback(() => {
    onSelect(therapist);
  }, [onSelect, therapist]);

  return (
    <button
      type="button"
      onClick={select}
      className="focus-ring flex w-full flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:border-[var(--accent)] hover:shadow-sm dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="flex w-full items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--accent)] text-sm font-bold text-white">
            {therapistInitials(therapist.fullName)}
          </span>
          <div>
            <p className="font-semibold text-slate-800 dark:text-white">{therapist.fullName}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {therapist.specialisations.length > 0 ? therapist.specialisations.join(', ') : '—'}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge
            label={t(`status.${therapist.status}`)}
            tone={therapist.status === 'active' ? 'green' : 'slate'}
          />
          <Badge
            label={t(`form.therapist.categoryOption.${therapist.category}`)}
            tone={therapist.category === 'consultant' ? 'amber' : 'violet'}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat value={String(therapist.clientsCount)} label={t('admin.therapists.clients')} />
        <Stat value={String(therapist.sessionsCount)} label={t('admin.therapists.sessions')} />
        <Stat
          value={rating !== null ? `${rating.toFixed(1)}★` : '—'}
          label={t('admin.therapists.rating')}
        />
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {joined !== '' ? t('admin.therapists.joined', { date: joined }) : ''}
        </span>
        {rating !== null && <Stars rating={rating} />}
      </div>
    </button>
  );
}
